import React from 'react';
import '../common.scss';
import './styles.scss';

import Components from './components';
import { useCustomComponent } from '@/hooks';

export interface RadioOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface RadioProps {
  name: string;
  required?: boolean;
  disabled?: boolean;
  value?: string;
  options: string[] | RadioOption[];
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  className?: string;
  'aria-label'?: string;
  'aria-describedby'?: string;
}

const RadioComponent: React.FC<RadioProps> = props => {
  const { value, options, onChange } = props;

  const normalizedOptions = options.map(option =>
    typeof option === 'string' ? { value: option, label: option } : option
  );

  const handleOnClick = (option: RadioOption) => {
    if (onChange) {
      const syntheticEvent = {
        target: { value: option.value },
        currentTarget: { value: option.value },
      } as React.ChangeEvent<HTMLInputElement>;
      onChange(syntheticEvent);
    }
  };

  return (
    <Components.Container {...props}>
      {normalizedOptions.map((option, index) => {
        const isSelected = value === option.value;

        return (
          <Components.Item
            key={`${option.value}-${index}`}
            {...props}
            option={option}
            isSelected={isSelected}
          >
            <Components.Input {...props} option={option} />
            <Components.Button
              {...props}
              isSelected={isSelected}
              option={option}
              onClick={() => handleOnClick(option)}
            />
          </Components.Item>
        );
      })}
    </Components.Container>
  );
};

export const Radio: React.FC<RadioProps> = (props: RadioProps) => {
  const Component = useCustomComponent({
    componentName: 'Radio',
    Component: RadioComponent,
  });

  return <Component {...props} />;
};
